// Reconnect-driven drain of the offline meal outbox (issue #142).
//
// Mounts once at the app root. The hook drains the outbox on:
//   - mount (in case the page loaded online with stale entries from a
//     previous session)
//   - every `online` event from `globalThis`
// On a successful drain it invalidates the meal/day-summary/goal queries
// so the UI reflects the freshly-committed entries.

import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { drainMealOutbox } from './meal-outbox';

export function useDrainOutboxOnReconnect(): void {
  const qc = useQueryClient();
  useEffect(() => {
    // `cancelled` flag is the standard React cleanup pattern: when the
    // component unmounts mid-drain, we suppress the post-drain query
    // invalidation since `qc` may be torn down by the time we resolve.
    // eslint-disable-next-line functional/no-let
    let cancelled = false;
    const drain = (): void => {
      void (async () => {
        const outcome = await drainMealOutbox().catch(() => null);
        if (cancelled) return;
        if (outcome && outcome.succeeded > 0) {
          await qc.invalidateQueries({ queryKey: ['meals'] });
          await qc.invalidateQueries({ queryKey: ['day-summary'] });
          await qc.invalidateQueries({ queryKey: ['goals'] });
        }
      })();
    };

    if (typeof navigator !== 'undefined' && navigator.onLine) drain();
    if (typeof globalThis !== 'undefined') {
      globalThis.addEventListener('online', drain);
    }
    return () => {
      cancelled = true;
      if (typeof globalThis !== 'undefined') {
        globalThis.removeEventListener('online', drain);
      }
    };
  }, [qc]);
}
